import React, { useState, useEffect } from 'react'
import _ from 'lodash';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Modal,
  Table,
  Upload,
  Divider,
  Popover,
  Select,
  Avatar
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import {
  viewLeadFilesAtom,
  notificationsAtom,
  addLeadFileAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { getLocalizedFileType, toFormattedLocalTime } from '../../../../functions';
import { fileTypes } from '../../../../utils';

function LeadFilesModal() {
  const { t, i18n } = useTranslation();
  const defaultFileData = {
    files: [],
    total: 0,
    page: 0,
    size: 20,
    file: null
  };
  const { Dragger } = Upload;
  const { Option } = Select;
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useRecoilState(viewLeadFilesAtom);
  const [addFile, setAddFile] = useRecoilState(addLeadFileAtom);
  const [fileData, setFileData] = useState(defaultFileData);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);

  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: fileData.file ? [fileData.file] : [],
    beforeUpload: (file) => {
      setFileData({
        ...fileData,
        file
      });
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
      setFileData({
        ...fileData,
        file: null
      });
    }
  };

  function getFiles(page) {
    setFileData({
      ...fileData,
      page
    });
    setLoading(true);
    api.get(`leads/lead/files/${lead?.id}?page=${page}&size=${fileData.size}`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setFileData({
            ...fileData,
            total: response.data.data.total,
            files: response.data.data.files
          })
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function upload() {
    if (fileData.file) {
      const formdata = new FormData();
      formdata.append('file', fileData.file);
      formdata.append('type', selectedFileType);
      formdata.append('leadSubmissionId', lead?.id);
      setUploadingFile(true);
      api.post('leads/lead/files', formdata)
      .then((response) => {
        setUploadingFile(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          getFiles(0);
          setFileData({
            ...fileData,
            file: null
          });
        }
      })
      .catch((error) => {
        setUploadingFile(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
    }
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== fileData?.page) {
        getFiles(page - 1);
    } else {
      setFileData({
        ...fileData,
        size
      });
    }
  };

  useEffect(() => {
    setFileData(defaultFileData);
    setSelectedFileType('');
    if (lead) {
      setOpen(true);
      getFiles(0);
    } else {
      setOpen(false);
    }
  }, [lead]);

  useEffect(() => {
    if (!addFile && lead) {
      getFiles(fileData?.page);
    }
  }, [addFile]);

  useEffect(() => {
    if (lead) {
      getFiles(lead?.page);
    }
  }, [lead?.page]);

  return (
    <Modal
      width="80%"
      title={`${t('files')} ${lead?.fileCount > 0 ? `(${lead?.fileCount})` : ''}`}
      open={open}
      onCancel={() => {
        setLead(null);
      }}
      footer={[]}
    >
      <div className="flex flex-col">
        <Dragger className="h-42 md:h-28" disabled={uploadingFile} {...draggerProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t('click_or_drag_content')}
          </p>
        </Dragger>
        <Select
          className="w-2/3 md:w-1/3 mt-10 mx-auto"
          value={selectedFileType}
          disabled={uploadingFile}
          onChange={(obj) => setSelectedFileType(obj)}
        >
          <Option disabled value="">{t('select_file_type')}</Option>
          {_.map(_.orderBy(fileTypes, (f) => t(f).toLowerCase()), (g) => (
            <Option value={g}>{t(g)}</Option>
          ))}
        </Select>
        <Popover
          content={!fileData.file || selectedFileType === '' ? (
            <>
              {!fileData.file && (
                <p>{t('must_select_file')}</p>
              )}
              {selectedFileType === '' && (
                <p>{t('must_select_file_type')}</p>
              )}
            </>
          ) : null}
        >
          <Button
            disabled={!fileData.file || selectedFileType === ''}
            loading={uploadingFile}
            type="primary"
            className="w-2/3 md:w-1/3 mt-2 mx-auto"
            onClick={() => upload()}
          >
            {t('upload')}
          </Button>
        </Popover>
      </div>
      <Divider />
      <div className="flex">
        <Button
          size="small"
          type="primary"
          className="ml-auto mb-2"
          onClick={() => getFiles(0)}
        >
          {t('reload')}
        </Button>
      </div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: fileData?.size,
          showSizeChanger: true,
          current: fileData?.page + 1,
          total: fileData?.total,
          onChange: onTableChange
        }}
        dataSource={fileData?.files}
        columns={[
          {
            title: '',
            align: 'center',
            className: 'text-sm md:hidden',
            render: (row) => (
              <div>
                {row.fileName.includes('.pdf') && (
                  <button
                    type="button"
                    className="underline text-blue-500"
                    onClick={(e) => {
                      e.preventDefault();
                      const elems = row.attachmentUrl.split('/');
                      api.get(`leads/file?fileName=${elems[elems.length - 1]}`, { responseType: 'blob' })
                        .then((response) => {
                          const file = new Blob([response.data], { type: 'application/pdf' });
                          const fileURL = URL.createObjectURL(file);
                          window.open(fileURL, '_blank');
                        }).catch((error) => {
                          console.log(error);
                        });
                    }}
                  >
                    {row.fileName}
                  </button>
                )}
                {!row.fileName.includes('.pdf') && (
                  <a href={row.attachmentUrl} target="_blank" rel="noreferrer" className="underline text-blue-500">{row.fileName}</a>
                )}
                <p className="font-bold mt-4">
                  {t('type')}:
                  <span className="font-normal"> {getLocalizedFileType(row.type, t)}</span>
                </p>
                <p className="font-bold mt-4">
                  {t('uploaded_on')}:
                  <span className="font-normal"> {row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                </p>                
                <>
                  <span className="font-bold mt-2">{t('submitted_by')}: </span>
                  {row?.submittedBy && (
                    <span>
                      <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                      {row?.submittedBy?.completeName}
                    </span>
                  )}
                  {!row?.submittedBy && (
                    <span>-</span>
                  )}
                </>
              </div>
            ),
          },
          {
            title: t('uploaded_on'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
            ),
          },
          {
            title: t('file'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <>
                {/* <a href={row.attachmentUrl} target="_blank" rel="noreferrer" className="underline text-blue-500">{row.fileName}</a> */}
                {row.fileName.includes('.pdf') && (
                  <button
                    type="button"
                    className="underline text-blue-500"
                    onClick={(e) => {
                      e.preventDefault();
                      const elems = row.attachmentUrl.split('/');
                      api.get(`leads/file?fileName=${elems[elems.length - 1]}`, { responseType: 'blob' })
                        .then((response) => {
                          const file = new Blob([response.data], { type: 'application/pdf' });
                          const fileURL = URL.createObjectURL(file);
                          window.open(fileURL, '_blank');
                        }).catch((error) => {
                          console.log(error);
                        });
                    }}
                  >
                    {row.fileName}
                  </button>
                )}
                {!row.fileName.includes('.pdf') && (
                  <a href={row.attachmentUrl} target="_blank" rel="noreferrer" className="underline text-blue-500">{row.fileName}</a>
                )}
              </>
            ),
          },
          {
            title: t('type'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{getLocalizedFileType(row?.type, t)}</span>
            ),
          },
          {
            title: t('submitted_by'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <>
                {row?.submittedBy && (
                  <span>
                    <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                    {row?.submittedBy?.completeName}
                  </span>
                )}
                {!row?.submittedBy && (
                  <span>-</span>
                )}
              </>
            ),
          },
        ]}
      />
    </Modal>
  );
}

export default LeadFilesModal;
